import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ArrowLeft, Check, Upload, Wrench, Zap, Recycle, Hammer, Sprout, Flame, Home as HomeIcon, MoreHorizontal } from "lucide-react";

const TRADE_ICONS = { plumbing: Wrench, electrical: Zap, waste: Recycle, building: Hammer, gardening: Sprout, gas: Flame, roofing: HomeIcon, handyman: Wrench, other: MoreHorizontal };

const Field = ({ value, onChange, placeholder, type = "text", testid, autoFocus }) => (
  <input
    data-testid={testid}
    autoFocus={autoFocus}
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full border-b-2 border-border bg-transparent py-3 text-2xl font-heading font-bold text-onSurface focus:outline-none focus:border-brandPrimary transition-colors placeholder:text-borderStrong placeholder:font-semibold"
  />
);

export default function Onboarding() {
  const navigate = useNavigate();
  const { setUser, setAccess } = useAuth();
  const [trades, setTrades] = useState([]);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [form, setForm] = useState({ business_name: "", phone: "", trade: "", legal: {}, address: { line1: "", line2: "", city: "", postcode: "" } });

  useEffect(() => { api.get("/config/trades").then(({ data }) => setTrades(data.trades)); }, []);

  const tradeObj = trades.find((t) => t.key === form.trade);
  const legalFields = tradeObj?.legal_fields || [];
  const TOTAL = 6;

  const uploadLogo = async (file) => {
    if (!file) return;
    setLogoPreview(URL.createObjectURL(file));
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/logo", fd); toast.success("Logo uploaded"); }
    catch { toast.error("Logo upload failed"); }
  };

  const canNext = () => {
    if (step === 0) return form.business_name.trim().length > 1;
    if (step === 1) return form.phone.trim().length > 5;
    if (step === 2) return !!form.trade;
    if (step === 4) return legalFields.filter((f) => f.required).every((f) => (form.legal[f.key] || "").trim());
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/onboarding", { business_name: form.business_name, phone: form.phone, trade: form.trade, legal: form.legal, address: form.address });
      setUser(data.user); setAccess(data.access);
      toast.success("You're all set!");
      navigate("/home", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    finally { setSaving(false); }
  };

  const next = () => { if (step >= TOTAL - 1) submit(); else setStep((s) => s + 1); };

  return (
    <div className="min-h-screen bg-surface flex justify-center">
      <div className="w-full max-w-md min-h-screen flex flex-col px-6 pt-8 pb-8">
        <div className="flex items-center gap-3 mb-8">
          {step > 0 ? (
            <button data-testid="onb-back" onClick={() => setStep((s) => s - 1)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-surfaceTertiary">
              <ArrowLeft size={20} />
            </button>
          ) : <div className="w-9 h-9" />}
          <div className="flex-1 h-1.5 bg-surfaceTertiary rounded-full overflow-hidden">
            <div className="h-full bg-brandPrimary rounded-full transition-all duration-300" style={{ width: `${((step + 1) / TOTAL) * 100}%` }} />
          </div>
          <span className="text-xs font-semibold text-muted-foreground tabular-nums">{step + 1}/{TOTAL}</span>
        </div>

        <div key={step} className="flex-1 animate-fade-up">
          {step === 0 && (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-brandPrimary mb-2">Let's get set up</p>
              <h2 className="font-heading text-3xl font-extrabold text-onSurface mb-8 leading-tight">What's your business name?</h2>
              <Field autoFocus testid="onb-business-name" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} placeholder="e.g. Galbraith & Sons" />
            </>
          )}
          {step === 1 && (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-brandPrimary mb-2">Contact</p>
              <h2 className="font-heading text-3xl font-extrabold text-onSurface mb-8 leading-tight">Your phone number?</h2>
              <Field autoFocus testid="onb-phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="07123 456789" />
            </>
          )}
          {step === 2 && (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-brandPrimary mb-2">Your trade</p>
              <h2 className="font-heading text-3xl font-extrabold text-onSurface mb-6 leading-tight">What do you do?</h2>
              <div className="grid grid-cols-2 gap-3">
                {trades.map((t) => {
                  const Icon = TRADE_ICONS[t.key] || Wrench;
                  const active = form.trade === t.key;
                  return (
                    <button
                      key={t.key}
                      data-testid={`onb-trade-${t.key}`}
                      onClick={() => setForm({ ...form, trade: t.key, legal: {} })}
                      className={`flex flex-col items-start gap-3 p-4 rounded-[12px] border-2 text-left transition-all active:scale-[0.97] ${active ? "border-brandPrimary bg-brandTertiary/50" : "border-border bg-surface hover:border-borderStrong"}`}
                    >
                      <Icon size={24} className={active ? "text-brandPrimary" : "text-muted-foreground"} />
                      <span className="font-heading font-bold text-sm text-onSurface leading-tight">{t.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-brandPrimary mb-2">Branding</p>
              <h2 className="font-heading text-3xl font-extrabold text-onSurface mb-2 leading-tight">Add your logo</h2>
              <p className="text-sm text-muted-foreground mb-8">It'll appear on every PDF you send. You can skip this.</p>
              <label data-testid="onb-logo-drop" className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-borderStrong rounded-[20px] py-12 cursor-pointer hover:border-brandPrimary transition-colors">
                {logoPreview ? (
                  <img src={logoPreview} alt="logo" className="w-24 h-24 object-contain rounded-[12px]" />
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full bg-brandTertiary flex items-center justify-center"><Upload size={24} className="text-brandSecondary" /></div>
                    <span className="text-sm font-semibold text-onSurface">Tap to upload</span>
                    <span className="text-xs text-muted-foreground">PNG or JPG</span>
                  </>
                )}
                <input data-testid="onb-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              </label>
            </>
          )}
          {step === 4 && (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-brandPrimary mb-2">Compliance</p>
              <h2 className="font-heading text-3xl font-extrabold text-onSurface mb-2 leading-tight">Your registration details</h2>
              <p className="text-sm text-muted-foreground mb-8">These print on your documents. {legalFields.some((f) => f.required) ? "Required fields are marked." : "All optional — skip what you like."}</p>
              <div className="flex flex-col gap-6">
                {legalFields.map((f) => (
                  <div key={f.key}>
                    <label className="text-xs font-bold text-muted-foreground">{f.label} {f.required ? <span className="text-error">*</span> : <span className="text-borderStrong">(optional)</span>}</label>
                    <Field testid={`onb-legal-${f.key}`} value={form.legal[f.key] || ""} onChange={(v) => setForm({ ...form, legal: { ...form.legal, [f.key]: v } })} placeholder="" />
                  </div>
                ))}
                {legalFields.length === 0 && <p className="text-sm text-muted-foreground">Nothing needed for this trade.</p>}
              </div>
            </>
          )}
          {step === 5 && (
            <>
              <p className="text-xs font-bold uppercase tracking-widest text-brandPrimary mb-2">Address</p>
              <h2 className="font-heading text-3xl font-extrabold text-onSurface mb-8 leading-tight">Where are you based?</h2>
              <div className="flex flex-col gap-5">
                <Field autoFocus testid="onb-addr-line1" value={form.address.line1} onChange={(v) => setForm({ ...form, address: { ...form.address, line1: v } })} placeholder="Address line 1" />
                <Field testid="onb-addr-line2" value={form.address.line2} onChange={(v) => setForm({ ...form, address: { ...form.address, line2: v } })} placeholder="Address line 2" />
                <Field testid="onb-addr-city" value={form.address.city} onChange={(v) => setForm({ ...form, address: { ...form.address, city: v } })} placeholder="Town / City" />
                <Field testid="onb-addr-postcode" value={form.address.postcode} onChange={(v) => setForm({ ...form, address: { ...form.address, postcode: v } })} placeholder="Postcode" />
              </div>
            </>
          )}
        </div>

        <div className="pt-6 flex items-center gap-3">
          {step === 3 && (
            <button data-testid="onb-skip" onClick={next} className="text-sm font-semibold text-muted-foreground px-2">Skip</button>
          )}
          <button
            data-testid="onb-next"
            disabled={!canNext() || saving}
            onClick={next}
            className="flex-1 bg-brandPrimary text-onBrand rounded-full h-14 font-heading font-bold text-base flex items-center justify-center gap-2 hover:bg-brandSecondary transition-colors active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
          >
            {step >= TOTAL - 1 ? (saving ? "Setting up…" : <>Finish <Check size={20} /></>) : <>Continue <ArrowRight size={20} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
